import { reportError } from "@/utils/reportError";
import { trackNotificationPermission } from "@/utils/analytics";
import { registerForPushNotificationsAsync, savePushTokenToDatabase } from "@/hooks/usePushNotifications";
import { supabase } from "@/utils/supabase";
import { Ionicons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import * as Notifications from "expo-notifications";
import { Stack, useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
    ActivityIndicator,
    Platform,
    ScrollView,
    StyleSheet,
    Text,
    TouchableOpacity,
    View
} from "react-native";

interface BenefitItemProps {
  icon: keyof typeof Ionicons.glyphMap;
  title: string;
  description: string;
}

function BenefitItem({ icon, title, description }: BenefitItemProps) {
  return (
    <View style={styles.benefitItem}>
      <View style={styles.benefitIconContainer}>
        <Ionicons name={icon} size={24} color="#1C453F" />
      </View>
      <View style={styles.benefitTextContainer}>
        <Text style={styles.benefitTitle}>{title}</Text>
        <Text style={styles.benefitDescription}>{description}</Text>
      </View>
    </View>
  );
}

export default function NotificationPermissionScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const isWeb = Platform.OS === 'web';

  const checkRankingAndNavigate = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) {
        router.replace("/(tabs)");
        return;
      }

      const { data, error } = await supabase
        .from('profiles')
        .select('display_main_ranking')
        .eq('id', user.id)
        .single();
      
      // If error, profile doesn't exist, or display_main_ranking is null/undefined
      // Show ranking consent screen
      if (error || !data || data.display_main_ranking === null || data.display_main_ranking === undefined) {
        router.replace("/ranking-consent");
        return;
      }
      
      // Preference already set (true or false), go to tabs
      router.replace("/(tabs)");
    } catch (error) {
      reportError(error);
      // On error, still show ranking consent to be safe
      router.replace("/ranking-consent");
    }
  };

  const requestNotificationPermission = async () => {
    setIsLoading(true);
    try {
      if (isWeb) {
        trackNotificationPermission(false);
        await saveNotificationPreferences(false);
        await checkRankingAndNavigate();
        return;
      }

      const { status: existingStatus } =
        await Notifications.getPermissionsAsync();

      let finalStatus = existingStatus;
      
      if (existingStatus !== "granted") {
        const { status } = await Notifications.requestPermissionsAsync();
        finalStatus = status;
      }

      // Save preferences based on permission choice
      trackNotificationPermission(finalStatus === 'granted');
      await saveNotificationPreferences(finalStatus === 'granted');

      // If granted, immediately get and save the push token
      if (finalStatus === 'granted') {
        try {
          const token = await registerForPushNotificationsAsync();
          if (token) {
            await savePushTokenToDatabase(token);
          }
        } catch (err: any) {
          reportError(err);
        }
      }

      await checkRankingAndNavigate();
    } catch (error) {
      reportError(error);
      router.replace("/(tabs)");
    } finally {
      setIsLoading(false);
    }
  };

  const skipNotifications = async () => {
    // User skipped, set all preferences to false
    await saveNotificationPreferences(false);
    checkRankingAndNavigate();
  };

  const saveNotificationPreferences = async (accepted: boolean) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) return;

      // Set all preferences based on acceptance
      await supabase
        .from('notification_preferences')
        .upsert({
          user_id: user.id,
          quiz_enabled: accepted,
          objectives_enabled: accepted,
          wheel_enabled: accepted,
        }, {
          onConflict: 'user_id',
        });

    } catch (error) {
      reportError(error);
    }
  };

  return (
    <View style={styles.container}>
      <Stack.Screen options={{ headerShown: false }} />
      <StatusBar style="light" />
      <LinearGradient
        colors={["#1C453F", "#78A584"]}
        start={{ x: 0, y: 0 }}
        end={{ x: 0, y: 1 }}
        style={styles.background}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          {/* Icon Container */}
          <View style={styles.iconContainer}>
            <View style={styles.iconCircle}>
              <Ionicons name="notifications" size={64} color="#1C453F" />
            </View>
          </View>

          <Text style={styles.title}>
            {t(isWeb ? "notificationPermission.webTitle" : "notificationPermission.title")}
          </Text>

          <Text style={styles.subtitle}>
            {t(isWeb ? "notificationPermission.webSubtitle" : "notificationPermission.subtitle")}
          </Text>

          <View style={styles.benefitsContainer}>
            {isWeb ? (
              <BenefitItem
                icon="globe-outline"
                title={t("notificationPermission.webBenefitTitle")}
                description={t("notificationPermission.webBenefitDescription")}
              />
            ) : (
              <>
                <BenefitItem
                  icon="calendar-outline"
                  title={t("notificationPermission.benefitDailyTitle")}
                  description={t("notificationPermission.benefitDailyDescription")}
                />
                <BenefitItem
                  icon="trophy-outline"
                  title={t("notificationPermission.benefitSuccessTitle")}
                  description={t("notificationPermission.benefitSuccessDescription")}
                />
                <BenefitItem
                  icon="people-outline"
                  title={t("notificationPermission.benefitCommunityTitle")}
                  description={t("notificationPermission.benefitCommunityDescription")}
                />
                <BenefitItem
                  icon="gift-outline"
                  title={t("notificationPermission.benefitRewardsTitle")}
                  description={t("notificationPermission.benefitRewardsDescription")}
                />
              </>
            )}
          </View>

          <View style={styles.privacyContainer}>
            <Ionicons name="shield-checkmark-outline" size={20} color="#FFF" />
            <Text style={styles.privacyText}>
              {t(isWeb ? "notificationPermission.webPrivacyNote" : "notificationPermission.privacyNote")}
            </Text>
          </View>
        </ScrollView>

        <View style={styles.buttonsContainer}>
          <TouchableOpacity
            onPress={requestNotificationPermission}
            disabled={isLoading}
            style={styles.primaryButton}
            activeOpacity={0.8}
          >
            {isLoading ? (
              <ActivityIndicator color="#1C453F" />
            ) : (
              <Text style={styles.primaryButtonText}>
                {t(isWeb ? "common.continue" : "notificationPermission.enableButton")}
              </Text>
            )}
          </TouchableOpacity>

          {!isWeb && (
            <TouchableOpacity
              onPress={skipNotifications}
              style={styles.skipButton}
              disabled={isLoading}
            >
              <Text style={styles.skipButtonText}>{t("common.later")}</Text>
            </TouchableOpacity>
          )}
        </View>
      </LinearGradient>
    </View>
  );
}



const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  background: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    paddingHorizontal: 24,
    paddingTop: 60,
    paddingBottom: 20,
  },
  iconContainer: {
    alignItems: "center",
    marginBottom: 32,
    marginTop: 20,
  },
  iconCircle: {
    width: 120,
    height: 120,
    borderRadius: 60,
    backgroundColor: "#FFF",
    alignItems: "center",
    justifyContent: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
    elevation: 8,
  },
  title: {
    fontFamily: "Inter_700Bold",
    fontSize: 32,
    color: "#FFF",
    textAlign: "center",
    marginBottom: 16,
    lineHeight: 40,
  },
  subtitle: {
    fontFamily: "Inter_400Regular",
    fontSize: 17,
    color: "rgba(255, 255, 255, 0.9)",
    textAlign: "center",
    lineHeight: 24,
    marginBottom: 40,
    paddingHorizontal: 20,
  },
  benefitsContainer: {
    marginBottom: 32,
  },
  benefitItem: {
    flexDirection: "row",
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    borderRadius: 16,
    padding: 16,
    marginBottom: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  benefitIconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: "#FBEFE3",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 16,
  },
  benefitTextContainer: {
    flex: 1,
    justifyContent: "center",
  },
  benefitTitle: {
    fontFamily: "Inter_600SemiBold",
    fontSize: 16,
    color: "#1C453F",
    marginBottom: 4,
  },
  benefitDescription: {
    fontFamily: "Inter_400Regular",
    fontSize: 14,
    color: "#666",
    lineHeight: 20,
  },
  privacyContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "rgba(255, 255, 255, 0.15)",
    borderRadius: 12,
    padding: 16,
    marginTop: 8,
  },
  privacyText: {
    fontFamily: "Inter_400Regular",
    fontSize: 13,
    color: "#FFF",
    flex: 1,
    marginLeft: 12,
    lineHeight: 18,
  },
  buttonsContainer: {
    paddingHorizontal: 24,
    paddingBottom: 40,
    paddingTop: 20,
  },
  primaryButton: {
    backgroundColor: "#FFF",
    borderRadius: 12,
    paddingVertical: 16,
    alignItems: "center",
    marginBottom: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  primaryButtonText: {
    fontFamily: "Inter_700Bold",
    fontSize: 16,
    color: "#1C453F",
  },
  skipButton: {
    paddingVertical: 16,
    alignItems: "center",
  },
  skipButtonText: {
    fontFamily: "Inter_600SemiBold",
    fontSize: 16,
    color: "rgba(255, 255, 255, 0.9)",
  },
});
